<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';
import TelegramCampaignTemplateLibraryModal from './components/TelegramCampaignTemplateLibraryModal.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();

const selectedCampaign = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);
const searchQuery = ref('');
const showTemplateLibrary = ref(false);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const telegramCampaigns = computed(
  () => getters['campaigns/getTelegramCampaigns'].value
);

const filteredTelegramCampaigns = computed(() => {
  const list = Array.isArray(telegramCampaigns.value)
    ? telegramCampaigns.value
    : [];
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return list;

  return list.filter(campaign =>
    [campaign?.title, campaign?.message, campaign?.inbox?.name]
      .filter(Boolean)
      .some(value => value.toString().toLowerCase().includes(query))
  );
});

const hasNoTelegramCampaigns = computed(
  () =>
    filteredTelegramCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleCreate = () => {
  router.push({ name: 'campaigns_telegram_new', params: route.params });
};

const handleEdit = campaign => {
  router.push({
    name: 'campaigns_telegram_edit',
    params: { ...route.params, id: campaign.id },
  });
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const openTemplateLibrary = () => {
  showTemplateLibrary.value = true;
};

const closeTemplateLibrary = () => {
  showTemplateLibrary.value = false;
};

const applyTemplateCampaign = campaign => {
  if (!campaign?.id) return;
  closeTemplateLibrary();
  router.push({
    name: 'campaigns_telegram_new',
    params: route.params,
    query: { template_from: campaign.id },
  });
};

const handleDuplicate = campaign => {
  applyTemplateCampaign(campaign);
};

const handleReport = campaign => {
  if (!campaign?.id) return;
  router.push({
    name: 'campaigns_telegram_report',
    params: { ...route.params, id: campaign.id },
  });
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.TELEGRAM.HEADER_TITLE')"
    :header-description="t('CAMPAIGN.TELEGRAM.EMPTY_STATE.SUBTITLE')"
    :button-label="t('CAMPAIGN.TELEGRAM.NEW_CAMPAIGN')"
    header-icon="i-ph-paper-plane-right-bold"
    @click="handleCreate"
  >
    <template #header-actions-left>
      <button
        class="template-library-btn"
        type="button"
        @click="openTemplateLibrary"
      >
        <span class="i-ph-layout-bold size-4" />
        Template library
      </button>
    </template>

    <template #toolbar>
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-xs">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 text-n-slate-11 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
            :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          />
        </div>
        <div class="text-sm text-n-slate-11 self-center">
          {{ filteredTelegramCampaigns.length }} campaigns
        </div>
      </div>
    </template>

    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoTelegramCampaigns"
      :campaigns="filteredTelegramCampaigns"
      is-editable
      is-reportable
      show-total-clicks
      @edit="handleEdit"
      @duplicate="handleDuplicate"
      @report="handleReport"
      @delete="handleDelete"
    />
    <SMSCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.TELEGRAM.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.TELEGRAM.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />

    <TelegramCampaignTemplateLibraryModal
      :show="showTemplateLibrary"
      :campaigns="telegramCampaigns"
      :account-id="route.params.accountId"
      @close="closeTemplateLibrary"
      @apply="applyTemplateCampaign"
    />
  </CampaignLayout>
</template>

<style scoped>
.search-input {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}

.search-input:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.12);
  background: #ffffff !important;
}

.search-input::placeholder {
  color: #9ca3af !important;
}

.template-library-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  border-radius: 10px;
  height: 36px;
  padding: 0 0.9rem;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.template-library-btn:hover {
  background: #f9fafb;
}
</style>
